import {
  Chip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router';

import { useOutstandingCount } from '@/features/outstanding';

import { AccountBlock } from './AccountBlock';
import { accountNavigation, primaryNavigation, type NavItem } from './navigation';
import { useAccountSurface } from './useAccountMenu';

interface NavRowProps extends Pick<NavItem, 'label' | 'to' | 'end'> {
  icon: NavItem['icon'];
  /** Trailing content, e.g. the outstanding count. */
  children?: ReactNode;
}

/** One sidebar destination. Shared by the main list and the account list at the
 *  foot so a row never renders two different ways depending on which it is in. */
const NavRow = ({ label, to, icon: Icon, end, children }: NavRowProps) => (
  <ListItemButton
    component={NavLink}
    to={to}
    end={end ?? false}
    // A soft grey pill marks the active row — no bar, no fill of its own hue.
    // Inactive rows carry a grey label with a LIGHTER grey icon: the two-tone
    // treatment is what keeps a short menu from reading as a wall of equally
    // weighted links, and it is why the icon column recedes while the labels
    // stay scannable.
    className="rounded-lg px-2.5 py-1.5 text-content hover:bg-brand-100 aria-[current=page]:bg-brand-200 aria-[current=page]:font-semibold"
  >
    {/* Fixed-width, centred slot. Icon glyphs differ in width, and letting each
        size itself leaves every label starting at a slightly different x — the
        column of text visibly steps in and out down the menu. */}
    <ListItemIcon className="mr-2.5 min-w-6 justify-center text-brand-400 [.active_&]:text-content">
      <Icon className="text-xl" />
    </ListItemIcon>
    <ListItemText
      primary={label}
      slotProps={{
        primary: {
          className: 'whitespace-nowrap [.active_&]:font-semibold',
          sx: { fontSize: 14 },
        },
      }}
    />
    {children}
  </ListItemButton>
);

export const SideNav = () => {
  const outstanding = useOutstandingCount();
  const surface = useAccountSurface();

  return (
    <aside className="hidden w-(--spacing-sidebar) shrink-0 border-r border-divider bg-surface md:block">
      <div className="sticky top-header flex h-below-header flex-col p-2 pt-6">
        <Typography
          variant="overline"
          color="text.secondary"
          component="h2"
          className="mb-6 pl-2.5 text-xs font-bold tracking-wider"
        >
          Menu
        </Typography>

        <List component="nav" aria-label="Sidebar" className="flex flex-col gap-0.5 p-0">
          {primaryNavigation.map(({ label, to, icon: Icon, end }) => (
            <NavRow key={to} label={label} to={to} icon={Icon} end={end}>
              {/* Trailing rather than on the icon: the sidebar row is wide, and
                  a count at the end of it reads as a quantity instead of
                  crowding the glyph. */}
              {to === '/' && outstanding > 0 && (
                <Chip label={outstanding} size="small" color="error" className="ml-2" />
              )}
            </NavRow>
          ))}
        </List>

        <div className="flex-1" />

        {/* Settings sits at the FOOT of the sidebar, directly above the account
            block's rule. It is a destination like any other — reachable in one
            click rather than only from behind the account menu's ⋮ — but it is
            not day-to-day work, so it is separated from Outstanding and
            Companies by the gap rather than listed alongside them. */}
        <List component="nav" aria-label="Account" className="flex flex-col gap-0.5 p-0">
          <NavRow
            label={accountNavigation.settings.label}
            to={accountNavigation.settings.to}
            icon={accountNavigation.settings.icon}
          />
        </List>

        {surface === 'sidebar' && <AccountBlock />}
      </div>
    </aside>
  );
};
